import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowLeft, MessageSquare, Phone } from "lucide-react";
import { toast } from "sonner";

// Países más usados en peritaciones desde España. El primero es siempre el default.
const COUNTRY_PREFIXES = [
  { code: "ES", dial: "+34", flag: "🇪🇸", name: "España" },
  { code: "PT", dial: "+351", flag: "🇵🇹", name: "Portugal" },
  { code: "FR", dial: "+33", flag: "🇫🇷", name: "Francia" },
  { code: "IT", dial: "+39", flag: "🇮🇹", name: "Italia" },
  { code: "DE", dial: "+49", flag: "🇩🇪", name: "Alemania" },
  { code: "GB", dial: "+44", flag: "🇬🇧", name: "Reino Unido" },
  { code: "NL", dial: "+31", flag: "🇳🇱", name: "Países Bajos" },
  { code: "BE", dial: "+32", flag: "🇧🇪", name: "Bélgica" },
  { code: "CH", dial: "+41", flag: "🇨🇭", name: "Suiza" },
  { code: "AT", dial: "+43", flag: "🇦🇹", name: "Austria" },
  { code: "IE", dial: "+353", flag: "🇮🇪", name: "Irlanda" },
  { code: "AD", dial: "+376", flag: "🇦🇩", name: "Andorra" },
  { code: "MA", dial: "+212", flag: "🇲🇦", name: "Marruecos" },
  { code: "RO", dial: "+40", flag: "🇷🇴", name: "Rumanía" },
  { code: "PL", dial: "+48", flag: "🇵🇱", name: "Polonia" },
  { code: "US", dial: "+1", flag: "🇺🇸", name: "EE. UU." },
  { code: "MX", dial: "+52", flag: "🇲🇽", name: "México" },
  { code: "AR", dial: "+54", flag: "🇦🇷", name: "Argentina" },
  { code: "CO", dial: "+57", flag: "🇨🇴", name: "Colombia" },
  { code: "CL", dial: "+56", flag: "🇨🇱", name: "Chile" },
  { code: "PE", dial: "+51", flag: "🇵🇪", name: "Perú" },
  { code: "BR", dial: "+55", flag: "🇧🇷", name: "Brasil" },
];

const DEFAULT_PREFIX = COUNTRY_PREFIXES[0].dial; // +34

const NO_COMPANY = "__none__";

export default function NewSiniestro() {
  const nav = useNavigate();
  const [prefix, setPrefix] = useState(DEFAULT_PREFIX);
  const [telLocal, setTelLocal] = useState("");
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(NO_COMPANY);
  const [policyTemplateId, setPolicyTemplateId] = useState("");
  const [baremoId, setBaremoId] = useState("");
  const [form, setForm] = useState({
    expediente_num: "",
    asegurado_nombre: "",
    descripcion: "",
    canal: "sms",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data?.items || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const tel = `${prefix} ${telLocal.replace(/\s+/g, " ").trim()}`.trim();
    if (!telLocal.trim()) {
      toast.error("Introduce el número de teléfono del asegurado");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        asegurado_telefono: tel,
        base_url: window.location.origin,
      };
      if (companyId && companyId !== NO_COMPANY) {
        payload.compania_id = companyId;
        if (policyTemplateId) payload.policy_template_id = policyTemplateId;
        if (baremoId) payload.baremo_id = baremoId;
      }
      const r = await api.post("/siniestros", payload);
      const s = r.data;
      if (s.invite_sent) {
        toast.success(`Enlace enviado por ${s.canal === "whatsapp" ? "WhatsApp" : "SMS"} a ${s.asegurado_telefono}`);
      } else {
        toast.warning(`No se pudo enviar el SMS/WhatsApp: ${s.invite_error || "revisa credenciales Twilio"}. Puedes copiar el enlace manualmente.`);
      }
      nav(`/siniestros/${s.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear siniestro");
    } finally {
      setLoading(false);
    }
  };

  const selectedCountry = COUNTRY_PREFIXES.find(c => c.dial === prefix) || COUNTRY_PREFIXES[0];
  const selectedCompany = companies.find((c) => c.id === companyId);
  const policyTemplates = selectedCompany?.policy_templates || [];
  // Orden alfabético español memoizado para no rehacer el sort en cada render.
  const policyTemplatesSorted = useMemo(
    () => [...policyTemplates].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })),
    [policyTemplates]
  );
  const companyBaremos = selectedCompany?.baremos || [];

  // Resetea la plantilla y el baremo cuando cambias de compañía
  useEffect(() => { setPolicyTemplateId(""); setBaremoId(""); }, [companyId]);

  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/" className="text-sm text-[#7A786F] hover:text-[#2C2C2A] inline-flex items-center mb-3">
        <ArrowLeft className="h-4 w-4 mr-1" /> Volver
      </Link>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-[#2C2C2A]">Nueva peritación</h1>
      <p className="text-[#7A786F] mt-1">Cuéntanos del expediente. Le mandamos el enlace al asegurado automáticamente.</p>

      <form onSubmit={submit} className="bg-white border border-[#E5E2D8] rounded-[14px] p-8 mt-6 space-y-5" data-testid="new-siniestro-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="exp">Expediente *</Label>
            <Input id="exp" required value={form.expediente_num}
              onChange={(e) => setForm({ ...form, expediente_num: e.target.value })}
              placeholder="EXP-2026-00123" data-testid="exp-num-input"
              className="rounded-[12px] border-[#E5E2D8]" />
          </div>
          <div>
            <Label htmlFor="nom">Nombre del asegurado *</Label>
            <Input id="nom" required value={form.asegurado_nombre}
              onChange={(e) => setForm({ ...form, asegurado_nombre: e.target.value })}
              placeholder="María García" data-testid="asegurado-nombre-input"
              className="rounded-[12px] border-[#E5E2D8]" />
          </div>
          <div>
            <Label htmlFor="tel">Teléfono móvil *</Label>
            <div className="flex gap-2">
              <Select value={prefix} onValueChange={setPrefix}>
                <SelectTrigger
                  className="w-[130px] rounded-[12px] border-[#E5E2D8] shrink-0"
                  data-testid="asegurado-prefix-select"
                  aria-label="Prefijo internacional"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-base leading-none" aria-hidden>{selectedCountry.flag}</span>
                    <span className="font-mono text-sm">{selectedCountry.dial}</span>
                  </span>
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  {COUNTRY_PREFIXES.map((c) => (
                    <SelectItem key={c.code} value={c.dial} data-testid={`prefix-option-${c.code}`}>
                      <span className="flex items-center gap-2">
                        <span className="text-base leading-none" aria-hidden>{c.flag}</span>
                        <span className="text-sm">{c.name}</span>
                        <span className="font-mono text-xs text-[#7A786F] ml-auto">{c.dial}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="tel"
                required
                inputMode="tel"
                autoComplete="tel-national"
                value={telLocal}
                onChange={(e) => setTelLocal(e.target.value)}
                placeholder="612 345 678"
                data-testid="asegurado-tel-input"
                className="rounded-[12px] border-[#E5E2D8] flex-1"
              />
            </div>
            <p className="text-xs text-[#7A786F] mt-1">El número se enviará como <span className="font-mono">{prefix} {telLocal || "…"}</span></p>
          </div>
          <div>
            <Label htmlFor="canal">Canal de envío</Label>
            <Select value={form.canal} onValueChange={(v) => setForm({ ...form, canal: v })}>
              <SelectTrigger id="canal" data-testid="canal-select" className="rounded-[12px] border-[#E5E2D8]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sms"><Phone className="h-3.5 w-3.5 mr-2 inline" /> SMS</SelectItem>
                <SelectItem value="whatsapp"><MessageSquare className="h-3.5 w-3.5 mr-2 inline" /> WhatsApp</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="company">Compañía aseguradora</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger id="company" data-testid="company-select" className="rounded-[12px] border-[#E5E2D8]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_COMPANY} data-testid="company-option-none">Otra / Sin compañía</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.slug}`}>
                    {c.name}
                    {c.has_baremo && (
                      <span className="ml-2 text-[10px] text-[#1D9E75]">· baremo {c.baremo_entries_count}</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#7A786F] mt-1">
              Si la compañía tiene baremo cargado, la IA usará sus precios al valorar.
              {companies.length === 0 && " (Ninguna compañía cargada todavía — añádelas en Admin → Compañías.)"}
            </p>
          </div>
          {companyId !== NO_COMPANY && policyTemplates.length > 0 && (
            <div className="md:col-span-2">
              <Label htmlFor="policy-template">Modalidad de póliza suscrita</Label>
              <Select value={policyTemplateId || "__none__"} onValueChange={(v) => setPolicyTemplateId(v === "__none__" ? "" : v)}>
                <SelectTrigger id="policy-template" data-testid="policy-template-select" className="rounded-[12px] border-[#E5E2D8]"><SelectValue placeholder="Sin especificar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="policy-template-option-none">Sin especificar</SelectItem>
                  {policyTemplatesSorted.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`policy-template-option-${t.id}`}>{t.name}</SelectItem>
                  ))}
                  <SelectItem value="otra" data-testid="policy-template-option-otra">Otra póliza (criterios genéricos)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#7A786F] mt-1">
                La IA consultará el condicionado de esta póliza para referenciar coberturas o exclusiones.
              </p>
            </div>
          )}
          {companyId !== NO_COMPANY && companyBaremos.length > 0 && (
            <div className="md:col-span-2">
              <Label htmlFor="baremo">Baremo a aplicar</Label>
              <Select value={baremoId || "__none__"} onValueChange={(v) => setBaremoId(v === "__none__" ? "" : v)}>
                <SelectTrigger id="baremo" data-testid="baremo-select" className="rounded-[12px] border-[#E5E2D8]"><SelectValue placeholder="Sin baremo específico" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="baremo-option-none">Sin baremo específico (la IA usa precio de mercado)</SelectItem>
                  {companyBaremos.map((b) => (
                    <SelectItem key={b.id} value={b.id} data-testid={`baremo-option-${b.id}`}>
                      {b.name} <span className="text-[10px] text-[#7A786F] ml-1">({b.items_count} partidas)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#7A786F] mt-1">
                La IA usará SOLO los precios del baremo seleccionado al valorar este expediente.
              </p>
            </div>
          )}
        </div>
        <div>
          <Label htmlFor="desc">Descripción</Label>
          <Textarea id="desc" rows={4} value={form.descripcion}
            onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
            placeholder="Daños observados, vehículo, dirección…" data-testid="descripcion-input"
            className="rounded-[12px] border-[#E5E2D8]" />
        </div>
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" variant="outline" className="border-[#E5E2D8] rounded-[12px]" onClick={() => nav(-1)}>Cancelar</Button>
          <Button type="submit" disabled={loading} className="btn-eks-primary rounded-[12px]" data-testid="create-siniestro-submit">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Crear y enviar enlace
          </Button>
        </div>
      </form>
    </div>
  );
}
